import React, { useEffect, useState } from "react";
import "./index.css"
import { Card, Container, Image } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { goBack } from "../../utils/otherHelopers";
import { routes } from "../../routes/pageRoutes";
import { fetchListingDetails, clearListingDetails, moderateListing } from "../../redux/slice/marketplaceSlice";
import { useLoader } from "../../context/LoaderContext";
import { ArrowLeftIcon, CalendarIcon } from "../../assets/icons";
import { createPortal } from "react-dom";
import { formatMomentDate } from "../../utils/dateHelper";
import CommonButton from "../../components/commonButton";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, FreeMode } from "swiper/modules";
import {
    ChevronLeft,
    ChevronRight,
    Package,
    MapPin,
    LayoutGrid,
    FileText,
    CheckCircle2,
    XCircle,
    Calendar,
    CalendarCheck,
    Hash,
    Award,
    ClipboardX,
} from "lucide-react";
import { ProfileCard } from "../../components/custom-card/Cards";
import StatusChip from "../../components/statusChip/StatusChip";

const renderHeaderContent = (onBack) => (
    <div className="d-flex align-items-center justify-content-between w-100">
        <div className="d-flex align-items-center gap-2">
            <button
                onClick={onBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
            >
                <ArrowLeftIcon size={22} strokeWidth="2.2" />
            </button>
            <h4 className="fw-semibold mb-0" style={{ fontSize: '18px' }}>
                Marketplace Listing Details
            </h4>
        </div>

    </div>
);
const MarketplaceUserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { setLoading } = useLoader();

    const { details, isLoading } = useSelector((state) => state.marketplace);

    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (id) {
            setLoading(true);
            dispatch(fetchListingDetails({ id, seller_type: 'user' })).finally(() => setLoading(false));
        }
        return () => {
            dispatch(clearListingDetails());
        };
    }, [id, dispatch, setLoading]);

    const handleModerate = async (status) => {
        try {
            const rejection_reason = status === 'rejected' ? prompt("Enter rejection reason:") : null;
            if (status === 'rejected' && !rejection_reason) return;

            await dispatch(moderateListing({ id, status, rejection_reason, seller_type: 'user' })).unwrap();

            dispatch(fetchListingDetails({ id, seller_type: 'user' }));
        } catch (error) {
            // handle error if needed
        }
    };

    if (!details && !isLoading) {
        return <div className="p-5 text-center">Listing not found</div>;
    }

    const sellerInfo = details?.seller_type === 'user' ? details?.user : details?.company;
    const headerPortalTarget = document.getElementById("header-portal-target");
    const handleBack = () => {
        goBack(navigate, location, routes.marketplaceListings);
    };
    const photos = details?.photos || [];

    return (
        <>
            {headerPortalTarget && createPortal(renderHeaderContent(handleBack), headerPortalTarget)}
            <Container fluid className="px-0 grid gap-4 marketplace-user-details">
                {/* Left Column: Premium Main Listing Details Card, Rejection details, Video, brochure */}
                <div className="g-col-8 marketplace-listing-card border-0 grid common-shadow overflow-hidden">
                    {/* Left Column: Image Slider */}
                    <div className="marketplace-image-section g-col-7">
                        <div className="marketplace-main-swiper-wrapper position-relative rounded-3 overflow-hidden">
                            {photos.length > 0 ? (
                                <Swiper
                                    modules={[Navigation, Thumbs, FreeMode]}
                                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                    navigation={{
                                        nextEl: ".mp-thumb-next",
                                        prevEl: ".mp-thumb-prev"
                                    }}
                                    spaceBetween={0}
                                    slidesPerView={1}
                                    onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                                    className="mp-main-swiper h-100"
                                >
                                    {photos.map((photo, idx) => (
                                        <SwiperSlide key={photo.id || idx}>
                                            <div className="mp-main-image-slide w-100 h-100">
                                                <Image
                                                    src={photo.url}
                                                    alt={`Product photo ${idx + 1}`}
                                                    className="w-100 h-100 object-fit-cover"
                                                />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            ) : (
                                <div className="w-100 h-100 bg-light d-flex align-items-center justify-content-center text-muted">
                                    No Image Available
                                </div>
                            )}

                            {photos.length > 0 && (
                                <div className="mp-swiper-fraction">
                                    {activeIndex + 1} / {photos.length}
                                </div>
                            )}
                        </div>

                        {photos.length > 0 && (
                            <div className="mp-thumbs-swiper-container d-flex align-items-center mt-3 gap-2">
                                <button className="mp-thumb-nav-btn mp-thumb-prev">
                                    <ChevronLeft size={16} />
                                </button>
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    watchSlidesProgress={true}
                                    slidesPerView={5}
                                    spaceBetween={8}
                                    freeMode={true}
                                    modules={[FreeMode, Navigation, Thumbs]}
                                    className="mp-thumbs-swiper flex-grow-1"
                                >
                                    {photos.map((photo, idx) => (
                                        <SwiperSlide key={photo.id || idx} className="cursor-pointer">
                                            <div className={`mp-thumb-slide-wrapper rounded-2 overflow-hidden ${activeIndex === idx ? 'active' : ''}`}>
                                                <Image
                                                    src={photo.url}
                                                    alt={`Thumb ${idx + 1}`}
                                                    className="w-100 h-100 object-fit-cover"
                                                />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <button className="mp-thumb-nav-btn mp-thumb-next">
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Listing Details */}
                    <div className="marketplace-details-section g-col-5">
                        <StatusChip pill title={details?.status} bg={details?.status === "rejected" ? "danger" : details?.status === "published" ? "success" : "warning"} />
                        <h3 className="mp-details-title mb-2">
                            {details?.name}
                        </h3>

                        <div className="mp-subtitle-row d-flex flex-column gap-2 mb-4">
                            <div className="mp-subtitle-item d-flex align-items-center gap-1 text-muted">
                                <Package className="mp-subtitle-icon" size={16} />
                                <span>{details?.category?.name || "Uncategorized"}</span>
                            </div>

                            <div className="mp-subtitle-item d-flex align-items-center gap-1 text-muted">
                                <MapPin className="mp-subtitle-icon" size={16} />
                                <span>
                                    {details?.city?.name && details?.state?.name
                                        ? `${details.city.name}, ${details.state.name}`
                                        : "Location details unavailable"}
                                </span>
                            </div>
                        </div>

                        <div className="mp-section-group mb-4">
                            <div className="mp-section-label d-flex align-items-center gap-2 text-muted mb-2">
                                <LayoutGrid className="mp-section-icon" size={18} />
                                <span>Price</span>
                            </div>
                            <div className="mp-price-value fw-bold">
                                {details?.price ? `₹ ${details.price}` : "Negotiable"}
                            </div>
                        </div>

                        <div className="mp-section-group mb-4 flex-grow-1">
                            <div className="mp-section-label d-flex align-items-center gap-2 text-muted mb-2">
                                <FileText className="mp-section-icon" size={18} />
                                <span>Description</span>
                            </div>
                            <div className="mp-description-value text-secondary">
                                {details?.description || "No description provided."}
                            </div>
                        </div>

                        <hr className="border-top w-100 opacity-100 mt-3 mb-0" />




                        <div className="mp-actions-row d-flex gap-2">
                            {details?.status === 'pending' && (
                                <>
                                    <CommonButton
                                        title="Approve Listing"
                                        onClick={() => handleModerate('published')}
                                        variant="primary"
                                        className="flex-grow-1 py-2 justify-content-center"
                                        icon={<CheckCircle2 size={16} />}
                                    />
                                    <CommonButton
                                        title="Reject Listing"
                                        onClick={() => handleModerate('rejected')}
                                        variant="outline-primary"
                                        className="flex-grow-1 py-2 justify-content-center"
                                        icon={<XCircle size={16} />}
                                    />
                                </>
                            )}
                            {details?.status === 'published' && (
                                <CommonButton
                                    title="Reject Listing"
                                    onClick={() => handleModerate('rejected')}
                                    variant="outline-primary"
                                    className="flex-grow-1 py-2 justify-content-center"
                                    icon={<XCircle size={16} />}
                                />
                            )}
                            {details?.status === 'rejected' && (
                                <CommonButton
                                    title="Approve Listing"
                                    onClick={() => handleModerate('published')}
                                    variant="primary"
                                    className="flex-grow-1 py-2 justify-content-center"
                                    icon={<CheckCircle2 size={16} />}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <Card className="rounded-3 p-4 border-0 common-shadow g-col-4">
                    <div className="d-flex align-items-center gap-2 mb-4 text-primary fw-semibold fs-5">
                        <Calendar size={22} className="text-primary" />
                        <span>Posting Details</span>
                    </div>
                    <div className="info-list d-flex flex-column gap-2">
                        <div className="d-flex align-items-center justify-content-between pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2 text-muted">
                                <Calendar size={18} className="text-secondary" />
                                <span>Posted On</span>
                            </div>
                            <span className="fw-medium text-dark">
                                {details?.created_at ? formatMomentDate(details.created_at, "DD MMM YYYY, hh:mm A") : "-"}
                            </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2 text-muted">
                                <CalendarCheck size={18} className="text-secondary" />
                                <span>Last Updated</span>
                            </div>
                            <span className="fw-medium text-dark">
                                {details?.updated_at ? formatMomentDate(details.updated_at, "DD MMM YYYY, hh:mm A") : "-"}
                            </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2 text-muted">
                                <Hash size={18} className="text-secondary" />
                                <span>Listing ID</span>
                            </div>
                            <span className="fw-medium text-dark">
                                {details?.listing_code || details?.code || (details?.id ? `#LST-${details.id.toString().substring(0, 8)}` : "-")}
                            </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2 text-muted">
                                <Award size={18} className="text-secondary" />
                                <span>Status</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <StatusChip
                                    title={details?.status}
                                    bg={details?.status === "rejected" ? "danger" : details?.status === "published" ? "success" : "warning"}
                                    pill
                                    className="text-capitalize"
                                />
                            </div>
                        </div>
                    </div>
                </Card>
                {console.log(details?.user)}
                <ProfileCard title={"Seller Information"} className="g-col-4" user={{
                    name: `${details?.seller_type === 'user' ? `${details?.user?.first_name} ${details?.user?.last_name}` : details?.company?.name}`,
                    role: details?.seller_type === 'user'
                        ? details?.user?.profile?.profession?.name
                        : details?.seller_type
                    ,
                    image: details?.seller_type === 'user' ? details?.user?.profile?.profile_photo?.url : details?.company?.profile?.logo?.url,
                    email: sellerInfo?.email,
                    mobile_no: sellerInfo?.mobile_number,
                    ...(details?.seller_type === 'company' && {
                        website: details?.company?.website,
                        office_address: details?.company_seller_info?.office_address,
                    })
                }} link={details?.seller_type === 'user' && details?.user?.id ? `/user/user-detail/${details.user.id}` : details?.seller_type === 'company' && details?.company?.id ? `/company/${details.company.id}` : ""} />
               
                {details?.status === 'rejected' && details?.rejection_reason && (
                    <Card className="rounded-3 p-4 border-0 common-shadow g-col-12">
                        <div className="d-flex align-items-center gap-2 mb-3 text-primary fw-semibold fs-5">
                            <ClipboardX size={22} className="text-primary" />
                            <span>Rejection Details</span>
                        </div>
                        <div className="p-3 rounded-2 bg-danger-subtle border border-danger-subtle">
                            <div className="text-danger fw-bold small mb-1">Reason</div>
                            <div className="text-dark">{details.rejection_reason}</div>
                        </div>
                    </Card>
                )}
                <div className="g-col-8 d-flex flex-column gap-4">

                    {details?.video && (
                        <Card className="rounded-3 p-4 border-0 common-shadow">
                            <h6 className="fw-bold mb-3">Video</h6>
                            <div className="ratio ratio-16x9 border rounded overflow-hidden bg-light">
                                <video controls className="w-100 h-100">
                                    <source src={details.video.url} type={details.video.mime_type || "video/mp4"} />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </Card>
                    )}

                    {details?.document && (
                        <Card className="rounded-3 p-4 border-0 common-shadow">
                            <h6 className="fw-bold mb-3">Document / Brochure</h6>
                            <div className="p-3 border rounded d-flex align-items-center justify-content-between bg-light">
                                <div className="d-flex align-items-center gap-2">
                                    <CalendarIcon width={24} height={24} />
                                    <div>
                                        <p className="mb-0 fw-medium">{details.document.name || "Brochure"}</p>
                                        <p className="mb-0 text-muted small">
                                            {details.document.size ? `${(details.document.size / (1024 * 1024)).toFixed(2)} MB` : ""}
                                        </p>
                                    </div>
                                </div>
                                <Link href={details.document.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                    View Document
                                </Link>
                            </div>
                        </Card>
                    )}
                </div>

            </Container>
        </>
    );
};

export default MarketplaceUserDetails;
